import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Facilities.css";

class Facilities extends Component {
  state = {
    facilities: [],
    formData: {
      id: null,
      facility_name: "",
      facility_icon: "",
      facility_status: "1",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchFacilities();
  }

  /** Load all facilities */
  fetchFacilities = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const res = await axios.get("/posts/facilities");
      const data = res.data?.data || [];

      this.setState({
        facilities: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message || "Failed to load facilities.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** Handle form field changes */
  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState((prev) => ({
      formData: {
        ...prev.formData,
        [name]: value,
      },
      error: "",
    }));
  };

  /** Search / filter changes */
  handleSearchChange = (e) => {
    this.setState({
      searchTerm: e.target.value,
      currentPage: 1,
    });
  };

  handleStatusFilterChange = (e) => {
    this.setState({
      statusFilter: e.target.value,
      currentPage: 1,
    });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({
      itemsPerPage: Number(e.target.value),
      currentPage: 1,
    });
  };

  clearFilters = () => {
    this.setState({
      searchTerm: "",
      statusFilter: "all",
      currentPage: 1,
    });
  };

  /** Open Add/Edit modal */
  openModal = (mode = "add", facility = null) => {
    const modalElement = document.getElementById("facilitieModal");

    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);

    this.setState(
      {
        modalMode: mode,
        formData:
          mode === "edit" && facility
            ? {
                id: facility.id,
                facility_name: facility.facility_name || "",
                facility_icon: facility.facility_icon || "",
                facility_status:
                  facility.facility_status?.toString() || "1",
              }
            : {
                id: null,
                facility_name: "",
                facility_icon: "",
                facility_status: "1",
              },
        error: "",
      },
      () => modal.show()
    );
  };

  closeFacilityModal = () => {
    const modalElement = document.getElementById("facilitieModal");

    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }

    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        facility_name: "",
        facility_icon: "",
        facility_status: "1",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  /** Submit Add/Edit form */
  handleSubmit = async (e) => {
    e.preventDefault();

    const { formData, modalMode } = this.state;
    const facilityName = formData.facility_name.trim();
    const facilityIcon = formData.facility_icon.trim();

    if (!facilityName) {
      this.setState({ error: "Facility name is required." });
      return;
    }

    this.setState({
      submitting: true,
      error: "",
    });

    const payload = {
      ...formData,
      facility_name: facilityName,
      facility_icon: facilityIcon,
      facility_status: formData.facility_status?.toString() || "1",
    };

    try {
      if (modalMode === "add") {
        await axios.post("/posts/facilities", payload);
      } else {
        await axios.put(`/posts/facilities/${formData.id}`, payload);
      }

      await this.fetchFacilities();

      this.closeFacilityModal();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Facility added successfully!"
            : "Facility updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving facility.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** Confirm delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });

    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  /** Delete facility */
  handleDelete = async () => {
    const { deleteId } = this.state;

    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`/posts/facilities/${deleteId}`);

      await this.fetchFacilities();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Facility deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete facility.",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  /** Extract Phosphor icon classes from saved HTML */
  getIconClasses = (iconHtml = "") => {
    const classMatch = iconHtml.match(/class\s*=\s*["']([^"']+)["']/i);

    if (classMatch?.[1]) {
      return classMatch[1]
        .split(/\s+/)
        .filter((className) => /^ph(?:-[a-z0-9-]+)?$/i.test(className))
        .join(" ");
    }

    return iconHtml
      .trim()
      .split(/\s+/)
      .filter((className) => /^ph(?:-[a-z0-9-]+)?$/i.test(className))
      .join(" ");
  };

  /** Current filtered list */
  getFilteredFacilities = () => {
    const { facilities, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return facilities.filter((facility) => {
      const name = (facility.facility_name || "").toLowerCase();
      const icon = (facility.facility_icon || "").toLowerCase();
      const status = facility.facility_status?.toString() || "0";

      const matchesSearch =
        !keyword || name.includes(keyword) || icon.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  /** Pagination helper */
  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];

    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");

    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage } = this.state;
    const totalPages = Math.max(
      1,
      Math.ceil(this.getFilteredFacilities().length / this.state.itemsPerPage)
    );

    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;

    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));

    if (nextPage !== currentPage) {
      this.setState({ currentPage: nextPage });
    }
  };

  renderFacilityIcon = (iconHtml) => {
    const iconClasses = this.getIconClasses(iconHtml);

    if (!iconClasses) {
      return <i className="ph ph-buildings facility-icon-placeholder" />;
    }

    return <i className={`${iconClasses} facility-list-icon`} />;
  };

  render() {
    const {
      facilities,
      formData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredFacilities = this.getFilteredFacilities();
    const totalPages = Math.max(
      1,
      Math.ceil(filteredFacilities.length / itemsPerPage)
    );
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentFacilities = filteredFacilities.slice(
      startIndex,
      startIndex + itemsPerPage
    );
    const startRecord = filteredFacilities.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredFacilities.length);
    const activeCount = facilities.filter(
      (facility) => facility.facility_status?.toString() === "1"
    ).length;
    const inactiveCount = facilities.length - activeCount;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container facilities-page">
        <div className="pc-content">
          <div className="facilities-shell">
            {/* Header */}
            <div className="facilities-topbar">
              <div>
                <div className="facilities-breadcrumb">
                  <span>Management</span>
                  <i className="ph ph-caret-right" />
                  <strong>Facilities</strong>
                </div>
                <h2 className="facilities-title">Facilities</h2>
                <p className="facilities-subtitle">
                  Manage property facilities, icons and availability status.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Facility</span>
              </button>
            </div>

            {/* Summary */}
            <div className="facility-summary-grid">
              <div className="facility-summary-card">
                <div className="facility-summary-icon blue">
                  <i className="ph ph-buildings" />
                </div>
                <div>
                  <span className="summary-label">Total Facilities</span>
                  <strong>{facilities.length}</strong>
                </div>
              </div>

              <div className="facility-summary-card">
                <div className="facility-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="facility-summary-card">
                <div className="facility-summary-icon pink">
                  <i className="ph ph-pause-circle" />
                </div>
                <div>
                  <span className="summary-label">Inactive</span>
                  <strong>{inactiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Main card */}
            <div className="facility-card">
              <div className="facility-card-header">
                <div>
                  <h4>Facility List</h4>
                  <p>
                    {filteredFacilities.length} result
                    {filteredFacilities.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="facility-toolbar">
                <div className="facility-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="facility-search"
                    placeholder="Search facility name or icon..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="facility-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                      aria-label="Clear search"
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="facility-filter-group">
                  <label htmlFor="facilityStatusFilter">Status</label>
                  <select
                    id="facilityStatusFilter"
                    className="facility-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="facility-filter-group rows-control">
                  <label htmlFor="facilityRows">Rows</label>
                  <select
                    id="facilityRows"
                    className="facility-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="facility-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="facility-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchFacilities}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="facility-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading facilities...</span>
                </div>
              ) : (
                <>
                  <div className="facility-table-wrap">
                    <table className="facility-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th>Facility</th>
                          <th className="col-icon">Icon</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentFacilities.length ? (
                          currentFacilities.map((facility, index) => {
                            const isActive =
                              facility.facility_status?.toString() === "1";

                            return (
                              <tr key={facility.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>

                                <td>
                                  <div className="facility-name-cell">
                                    <div className="facility-avatar">
                                      {this.renderFacilityIcon(facility.facility_icon)}
                                    </div>
                                    <div>
                                      <span className="facility-name">
                                        {facility.facility_name || "Unnamed facility"}
                                      </span>
                                      <small>ID #{facility.id}</small>
                                    </div>
                                  </div>
                                </td>

                                <td className="col-icon">
                                  <div className="facility-icon-preview">
                                    {this.renderFacilityIcon(facility.facility_icon)}
                                  </div>
                                </td>

                                <td className="col-status">
                                  <span
                                    className={`facility-status-badge ${
                                      isActive ? "active" : "inactive"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>

                                <td className="col-actions">
                                  <div className="facility-actions">
                                    <button
                                      type="button"
                                      className="facility-action-btn edit"
                                      onClick={() => this.openModal("edit", facility)}
                                      title="Edit facility"
                                      aria-label={`Edit ${facility.facility_name}`}
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="facility-action-btn delete"
                                      onClick={() => this.confirmDelete(facility.id)}
                                      title="Delete facility"
                                      aria-label={`Delete ${facility.facility_name}`}
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5">
                              <div className="facility-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-buildings" />
                                </div>
                                <h5>No facilities found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search or filters."
                                    : "Add your first facility to get started."}
                                </p>
                                {searchTerm || statusFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="facility-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Facility
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {filteredFacilities.length > 0 && (
                    <div className="facility-pagination-wrap">
                      <div className="facility-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredFacilities.length}</strong> facilities
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Facilities pagination">
                          <ul className="facility-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                                aria-label="Previous page"
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>

                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={safeCurrentPage === page ? "active" : ""}
                                >
                                  <button
                                    type="button"
                                    onClick={() => this.goToPage(page)}
                                    aria-current={
                                      safeCurrentPage === page ? "page" : undefined
                                    }
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}

                            <li
                              className={
                                safeCurrentPage === totalPages ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                                aria-label="Next page"
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="facilitieModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content facility-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header facility-modal-header">
                  <div>
                    <span className="facility-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Facility" : "Edit Facility"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="facility-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    aria-label="Close"
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body facility-modal-body">
                  {error && (
                    <div className="facility-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="facility-form-group">
                    <label htmlFor="facilityName">
                      Facility Name <span>*</span>
                    </label>
                    <div className="facility-input-wrap">
                      <i className="ph ph-tag" />
                      <input
                        id="facilityName"
                        type="text"
                        name="facility_name"
                        className="facility-input"
                        placeholder="e.g. Air Conditioning (AC)"
                        value={formData.facility_name}
                        onChange={this.handleChange}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="facility-form-group">
                    <label htmlFor="facilityIcon">Facility Icon</label>
                    <div className="facility-input-wrap">
                      <i className="ph ph-code" />
                      <input
                        id="facilityIcon"
                        type="text"
                        name="facility_icon"
                        className="facility-input"
                        placeholder='<i class="ph ph-snowflake"></i>'
                        value={formData.facility_icon}
                        onChange={this.handleChange}
                        autoComplete="off"
                      />
                    </div>
                    <small className="facility-help-text">
                      Example: <code>&lt;i class="ph ph-snowflake"&gt;&lt;/i&gt;</code>
                    </small>

                    <div className="facility-icon-live-preview">
                      <span>Preview</span>
                      <div className="live-preview-icon">
                        {this.renderFacilityIcon(formData.facility_icon)}
                      </div>
                    </div>
                  </div>

                  <div className="facility-form-group mb-0">
                    <label htmlFor="facilityStatus">Status</label>
                    <div className="facility-status-options">
                      <label
                        className={`status-option ${
                          formData.facility_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="facility_status"
                          value="1"
                          checked={formData.facility_status === "1"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>

                      <label
                        className={`status-option ${
                          formData.facility_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="facility_status"
                          value="0"
                          checked={formData.facility_status === "0"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer facility-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i
                          className={
                            modalMode === "add"
                              ? "ph ph-plus"
                              : "ph ph-check"
                          }
                        />
                        {modalMode === "add" ? "Add Facility" : "Update Facility"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Facility?</h5>
                <p>
                  This facility will be permanently removed. This action cannot be
                  undone.
                </p>

                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Facilities;
